/**
 * Analytics Service
 * Tracks user interactions with the app by sending pageview events to the backend.
 * Implements retry logic, debouncing, and respects privacy by not storing PII.
 */

import { ANALYTICS_CONFIG } from '@/config/analytics'

const ANALYTICS_ENDPOINT = ANALYTICS_CONFIG.endpoint
const RETRY_ATTEMPTS = ANALYTICS_CONFIG.retryAttempts
const RETRY_DELAY_MS = ANALYTICS_CONFIG.retryDelayMs
const DEBOUNCE_DELAY_MS = ANALYTICS_CONFIG.debounceDelayMs

// Track pending requests to avoid duplicates
const pendingRequests = new Map()

/**
 * Sleep utility for retry delays
 */
function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Send analytics event with retry logic
 * @param {Object} payload - { name, path, ts?, user_id? }
 * @param {number} attempt - Current retry attempt
 */
async function sendAnalyticsWithRetry(payload, attempt = 1) {
    try {
        const token = localStorage.getItem('token')
        const headers = {
            'Content-Type': 'application/json'
        }
        
        if (token) {
            headers.Authorization = `JWT ${token}`
        }
        
        const response = await fetch(ANALYTICS_ENDPOINT, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload)
        })

        if (!response.ok) {
            throw new Error(`Analytics request failed: ${response.status}`)
        }

        const key = payload.moduleName

        pendingRequests.delete(key)

        if (process.env.NODE_ENV === 'development') {
            console.debug(`[Analytics] Pageview tracked: ${payload.moduleName}`)
        }

        return true
    } catch (error) {
        if (attempt < RETRY_ATTEMPTS) {
            await sleep(RETRY_DELAY_MS * attempt)

            return sendAnalyticsWithRetry(payload, attempt + 1)
        }

        console.warn(`[Analytics] Failed to track pageview after ${RETRY_ATTEMPTS} attempts:`, error)

        return false
    }
}

/**
 * Debounced analytics tracker
 * Prevents duplicate events for the same route within a short time window
 */
function createDebouncedTracker() {
    const timers = new Map()

    return function trackPageview(routeName) {
        // Check if analytics is enabled
        if (!ANALYTICS_CONFIG.enabled) {
            return
        }

        const key = `${routeName}`

        if (timers.has(key)) {
            clearTimeout(timers.get(key))
        }

        const timer = setTimeout(() => {
            if (!pendingRequests.has(key)) {
                pendingRequests.set(key, true)

                const payload = {
                    moduleName: routeName
                }

                sendAnalyticsWithRetry(payload)
            }

            timers.delete(key)
        }, DEBOUNCE_DELAY_MS)

        timers.set(key, timer)
    }
}

export const trackPageview = createDebouncedTracker()
